/**
 * Content Type Config API Client
 * Per-type tone, format and AI prompt context settings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ContentType } from './content';

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(endpoint, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || response.statusText);
  }
  if (response.status === 204) return null as T;
  return response.json();
}

function base(workspaceId: string) {
  return `/api/workspaces/${workspaceId}/content/type-configs`;
}

// =============================================================================
// Types
// =============================================================================

export interface ContentTypeConfig {
  id: string;
  workspace_id: string;
  content_type: ContentType;
  tone_instructions: string | null;
  format_guidance: string | null;
  ai_prompt_context: string | null;
  example_titles: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UpsertTypeConfigPayload {
  tone_instructions?: string | null;
  format_guidance?: string | null;
  ai_prompt_context?: string | null;
  example_titles?: string[];
  is_active?: boolean;
}

// =============================================================================
// API
// =============================================================================

export const contentTypeConfigsApi = {
  list(workspaceId: string): Promise<{ configs: ContentTypeConfig[] }> {
    return request<{ configs: ContentTypeConfig[] }>(base(workspaceId));
  },

  get(workspaceId: string, contentType: string): Promise<ContentTypeConfig> {
    return request<ContentTypeConfig>(`${base(workspaceId)}/${contentType}`);
  },

  upsert(workspaceId: string, contentType: string, data: UpsertTypeConfigPayload): Promise<ContentTypeConfig> {
    return request<ContentTypeConfig>(`${base(workspaceId)}/${contentType}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete(workspaceId: string, contentType: string): Promise<void> {
    return request<void>(`${base(workspaceId)}/${contentType}`, { method: 'DELETE' });
  },
};

// =============================================================================
// React Query hooks
// =============================================================================

export function useContentTypeConfigs(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['content-type-configs', workspaceId],
    queryFn: () => contentTypeConfigsApi.list(workspaceId!),
    enabled: !!workspaceId,
    select: data => data.configs,
  });
}

export function useContentTypeConfig(workspaceId: string | undefined, contentType: string | undefined) {
  return useQuery({
    queryKey: ['content-type-config', workspaceId, contentType],
    queryFn: () => contentTypeConfigsApi.get(workspaceId!, contentType!),
    enabled: !!workspaceId && !!contentType,
    retry: false,
  });
}

export function useUpsertTypeConfig(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contentType, data }: { contentType: string; data: UpsertTypeConfigPayload }) =>
      contentTypeConfigsApi.upsert(workspaceId!, contentType, data),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['content-type-configs', workspaceId] });
      qc.setQueryData(['content-type-config', workspaceId, saved.content_type], saved);
    },
  });
}

export function useDeleteTypeConfig(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contentType: string) =>
      contentTypeConfigsApi.delete(workspaceId!, contentType),
    onSuccess: (_data, contentType) => {
      qc.invalidateQueries({ queryKey: ['content-type-configs', workspaceId] });
      qc.removeQueries({ queryKey: ['content-type-config', workspaceId, contentType] });
    },
  });
}
